import React, { useEffect, useMemo, useRef, useState } from "react";
import paginationFactory, { PaginationProvider } from "react-bootstrap-table2-paginator";
import { entityFilter, getFilteredAndPaginatedEntities, getPaginationOptions, headerSortingClasses, sortCaret } from "../../../../../../../../_metronic/_helpers";
import * as columnFormatters from "./column-formatters";
import { Card, CardBody, CardHeader, CardHeaderToolbar, Pagination } from "../../../../../../../../_metronic/_partials/controls";
import { useDeploymentRTSPJobsUIContext } from "../DeploymentRTSPJobsUIContext";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import * as actions from "../../../../_redux/DeploymentRTSPJobs/DeploymentRTSPJobsAction";
import { SearchText } from "../../../../../../../../utils/SearchText";
import { AutoServingTable } from "../../../../../../../../utils/AutoServingTable";
import { Col } from "reactstrap";
import { fetchCompanies } from "../../../../../../../../utils/commonFunction";
import ReactSelectDropDownCommon from "../../../../../../../../utils/SuperAdmin/ReactSelectDropDownCommon";
import { fetchDeploymentJobs } from "../../../../_redux/DeploymentJobs/DeploymentJobsAction";
import CustomizedButtons from "../../../../../../../../utils/SuperAdmin/CustomizedButtons";

/**
 * Component to display a table of Deployment RTSP Jobs.
 *
 * @function DeploymentRTSPJobTable
 * @returns {JSX.Element} A table with columns for index, image size, IOU threshold, confidence threshold, model, deployment type, company, status, and actions.
 */
export function DeploymentRTSPJobTable() {
    const deploymentRTSPJobsUIContext = useDeploymentRTSPJobsUIContext();
    const deploymentRTSPJobsUIProps = useMemo(() => deploymentRTSPJobsUIContext, [
        deploymentRTSPJobsUIContext
    ]);

    const [selectedCompany, setSelectedCompany] = useState(null);
    const [companyOptions, setCompanyOptions] = useState([]);

    const deploymentRTSPJobUIProps = useMemo(() => {
        return {
            openNewDeploymentRTSPJobDialog:
                deploymentRTSPJobsUIContext.openNewDeploymentRTSPJobDialog
        };
    }, [deploymentRTSPJobsUIContext]);

    const { currentState } = useSelector(
        state => ({ currentState: state.deploymentRTSPJobs1 }),
        shallowEqual
    );
    const { entities, listLoading } = currentState;
    const [filterEntities, setFilterEntities] = useState(entities);
    const searchInput = useRef("");

    // Fetches and filters entities for pagination
    let currentItems = getFilteredAndPaginatedEntities(
        filterEntities || entities,
        deploymentRTSPJobsUIProps.queryParams
    );

    useEffect(() => {
        /**
         * Loads companies and sets the selected company in the component state.
         *
         * @function loadCompanies
         * @returns {Promise<void>} A promise that resolves when the companies have been loaded.
         */
        const loadCompanies = async () => {
            await fetchCompanies(setCompanyOptions, setSelectedCompany);
        };
        loadCompanies();
    }, []);

    // Table columns
    const columns = [
        {
            dataField: "idx",
            text: "Index",
            style: { minWidth: "55px" }
        },
        {
            dataField: "user_details.company.company_name",
            text: "Company",
            sort: true,
            sortCaret: sortCaret,
            headerSortingClasses
        },
        {
            dataField: "model_details.model_name",
            text: "Model",
            sort: true,
            sortCaret: sortCaret,
            headerSortingClasses,
            style: { minWidth: "150px" }
        },
        {
            dataField: "status",
            text: "Status",
            sort: true,
            sortCaret: sortCaret,
            headerSortingClasses,
            formatter: (cellContent, row, rowIndex) => (
                <span className={`label label-lg label-light-${row.status ? "primary" : "warning"} label-inline`}>
                    {row.status ? "Deployed" : "Not Deployed"}
                </span>
            ),
            style: { minWidth: "118px" }
        },
        {
            dataField: "action",
            text: "Actions",
            formatter: columnFormatters.ActionsColumnFormatter,
            formatExtraData: {
                openNewDeploymentRTSPJobDialog:
                    deploymentRTSPJobsUIProps.openNewDeploymentRTSPJobDialog,
                openViewDeploymentRTSPJobDialog:
                    deploymentRTSPJobsUIProps.openViewDeploymentRTSPJobDialog,
                openStartDeploymentRTSPJobDialog:
                    deploymentRTSPJobsUIProps.openStartDeploymentRTSPJobDialog
            },
            style: { minWidth: "152px" }
        }
    ];

    /**
     * Filters the deployment RTSP jobs based on the search input.
     *
     * @function filterDeploymentRTSPJobs
     * @param {Event} e - The event object from the search input change.
     */
    const filterDeploymentRTSPJobs = e => {
        const searchStr = e?.target?.value || searchInput.current.value;
        const keys = [
            "idx",
            "image_size",
            "iou_threshold",
            "confidence_threshold",
            "model_details.model_name",
            "deployment_type.deployment_type_name",
            "user_details.company_name"
        ];
        currentItems = entityFilter(
            entities || filterEntities,
            searchStr,
            keys,
            deploymentRTSPJobsUIProps.queryParams,
            setFilterEntities
        );
    };

    const dispatch = useDispatch();
    useEffect(() => {
        // Fetch deployment RTSP jobs
        dispatch(actions.fetchDeploymentRTSPJobs());
    }, [deploymentRTSPJobsUIProps.queryParams, dispatch]);

    useEffect(() => {
        if (selectedCompany?.value) {
            // Fetch deployment jobs for the selected company
            dispatch(fetchDeploymentJobs(selectedCompany.value))
                .then((jobs) => {
                    if (jobs.length > 0) {
                        console.log("Fetched jobs for local use:", jobs.length);
                        setFilterEntities(jobs);
                    } else {
                        console.log("Fetched jobs for local use: 0", jobs.length);
                        setFilterEntities([]);
                    }
                }).catch((error) => {
                    setFilterEntities([]);
                    console.error("Error fetching deployment jobs:", error);
                });
        }
    }, [selectedCompany, dispatch]);

    useEffect(() => {
        filterDeploymentRTSPJobs();
        // eslint-disable-next-line
    }, [entities]);

    /**
     * Handles company selection change.
     *
     * @param {Object} option - The selected company option.
     */
    const handleChangeCompany = (option) => {
        setSelectedCompany(option);
    }

    return (
        <>
            <Card>
                <CardHeader title="Deployment RTSP Jobs Data">
                    <CardHeaderToolbar>
                        <div className="d-flex align-items-center gap-3">
                            <div style={{ minWidth: "200px" }} className="mr-3">
                                <SearchText
                                    reference={searchInput}
                                    onChangeHandler={filterDeploymentRTSPJobs}
                                />
                            </div>

                            <CustomizedButtons
                                title="Add Deployment RTSP Job"
                                color="primary"
                                className="mr-3"
                                submit={deploymentRTSPJobUIProps.openNewDeploymentRTSPJobDialog}
                            />

                            <div style={{ minWidth: "180px" }}>
                                <ReactSelectDropDownCommon
                                    isSearchable={true}
                                    placeholder="Select Company"
                                    value={selectedCompany}
                                    onChange={handleChangeCompany}
                                    options={companyOptions}
                                />
                            </div>
                        </div>
                    </CardHeaderToolbar>
                </CardHeader>
                <CardBody>
                    <PaginationProvider
                        pagination={paginationFactory(
                            getPaginationOptions(
                                filterEntities?.length,
                                deploymentRTSPJobsUIProps.queryParams
                            )
                        )}
                    >
                        {({ paginationProps, paginationTableProps }) => {
                            return (
                                <Pagination
                                    isLoading={listLoading}
                                    paginationProps={paginationProps}
                                >
                                    <AutoServingTable
                                        columns={columns}
                                        items={currentItems}
                                        tableChangeHandler={deploymentRTSPJobsUIProps.setQueryParams}
                                        paginationTableProps={paginationTableProps}
                                    />
                                </Pagination>
                            );
                        }}
                    </PaginationProvider>
                </CardBody>
            </Card>
        </>
    );
}
